import usePublicEventService from "@app-contexts/public/features/event-detail/data/services/usePublicEventsService";
import EventStatuses from "@shared/components/ui-kit/data-display/EventStatuses/EventStatuses.web";
import { common, enums, PAYMENT_TYPE_OPTIONS_MAP } from "@shared/constants";
import { domElementIds } from "@shared/constants/common";
import { useMobileWebviewContext } from "@shared/contexts/MobileWebviewContext/MobileWebviewContext";
import { useEmbeddedComponent } from "@shared/hooks/useEmbeddedComponent";
import { useUserSessionAuthState } from "@shared/modules/auth/contexts/useUserSessionAuthState";
import { getEmbeddedPurchasePlatform } from "@shared/modules/financials/utils/purchase-platform-utils";
import { useTranslation } from "@shared/utils/internationalization";
import CardImage from "@ui-application/media/CardImage/CardImage.web";
import Button from "@ui-kit/form/Button/Button.web";
import Icon from "@ui-kit/icons/Icon.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { useRouter } from "next/router";
import { useState } from "react";
import ContactDetailsModalSelection from "../contactDetailsModal/ContactDetailsModalSelection";
import HelpAndInfoModalSelection from "../helpAndInfoModal/HelpAndInfoModalSelection";
import MapTimeslotModal from "../MapTimeslotModal/MapTimeslotModal.web";
import SeatingInfoModal from "../SeatingInfoModal/SeatingInfoModal.web";
import SeatingMainWrapper from "../SeatingLayout/SeatingMainWrapper/SeatingMainWrapper.web";
import SeatingSideWrapper from "../SeatingLayout/SeatingSideWrapper/SeatingSideWrapper.web";
import MoreInfoButton from "./buttons/MoreInfoButton.web";
import { MapBannerProps } from "./MapBanner.props";
import BannerSubtitle from "./text/BannerSubtitle.web";
import { useMapBanner } from "./useMapBanner";

const { seatingBannerId } = domElementIds;

const MapBanner = ({
  seatingObject,
  venueObject,
  mapItemType,
  isModal = false, // controls banner position on non-embedded modals
  hideBackButton = false,
  hideBannerImage = false,
  hideInfoButton = false,
  hideRefundPolicy = false,
}: MapBannerProps) => {
  const { t } = useTranslation("seating");

  const router = useRouter();
  const { webviewValueAdapter } = useMobileWebviewContext();
  const { isEmbedded } = useEmbeddedComponent();
  const { isLoggedIn, userProfileData: user } = useUserSessionAuthState();

  // hides/shows the read more button on the refund policy section
  const [hideReadMoreButton, setHideReadMoreButton] = useState(true);
  const updateHideReadMoreButton = (input: boolean) => {
    setHideReadMoreButton(input);
  };

  const eventParams = {
    // if on the seating widget, use widget logic for the purchase platform
    purchase_platform: isEmbedded
      ? getEmbeddedPurchasePlatform()
      : enums.PURCHASE_SOURCE_PLATFORM_MAP.PLATFORM_WEB.id,
    payment_type: [
      PAYMENT_TYPE_OPTIONS_MAP.CREDIT.id,
      PAYMENT_TYPE_OPTIONS_MAP.AFTERPAY.id,
    ].toString(),
  };

  const {
    refundPolicyId,
    refundPolicyText,
    parentEventData,
    getImageSource,
    isTimeslotModalOpen,
    onOpenTimeslotModal,
    onCloseTimeslotModal,
    onOpenInfoModal,
    onOpenHelpAndInfoModal,
    isInfoModalOpen,
    onCloseInfoModal,
    isHelpAndInfoModalOpen,
    onCloseHelpAndInfoModal,
    isContactDetailsModalOpen,
    onOpenContactDetailsModal,
    onCloseContactDetailsModal,
  } = useMapBanner({
    seatingObject,
    venueObject,
    mapItemType,
    hideBackButton,
    hideBannerImage,
    updateHideReadMoreButton,
  });

  // fetch parent event data for recurring assigned seating events
  const { useRetrieveWithMembershipTicketTypesOverride } =
    usePublicEventService();

  const isEventMap = mapItemType === common.SeatingMapItemType.EVENT;
  const eventSlugOrId = isEventMap ? seatingObject?.id : undefined;

  const { data: currentEventData } =
    useRetrieveWithMembershipTicketTypesOverride(eventSlugOrId, {
      config: {
        params: eventParams,
      },
      options: {
        enabled: isEventMap && Boolean(seatingObject && seatingObject.id),
      },
    });

  const eventStatuses = EventStatuses({ event_data: currentEventData });

  const handleRedirect = () => {
    // if no document referrer, then the user was routed through the site
    // use router history to go back to the previous page
    if (!document.referrer) {
      router.back();
    }
    // return to the membership detail page
    else if (
      document.referrer &&
      mapItemType === common.SeatingMapItemType.MEMBERSHIP
    ) {
      router.push(`/m/${seatingObject.slug}/`);
    }
    // return to the event detail page
    else if (
      document.referrer &&
      mapItemType === common.SeatingMapItemType.EVENT
    ) {
      // for recurring events, redirect to the parent event's detail page
      // else redirect to the seatingObject's detail page
      const eventSlugToUse = parentEventData?.slug ?? seatingObject.slug;
      router.push(`/${eventSlugToUse}/`);
    }
  };

  return (
    <Flex
      id={seatingBannerId}
      zIndex="navbarBanner"
      // Removing the "sticky" value for position styling in webview
      // as it leaves a large empty gap at top of page in mobile app.
      position={webviewValueAdapter({ web: "sticky" })}
      top={{
        base: isEmbedded || isModal ? "0" : "layout.navbarMobileHeight",
        sm: isEmbedded || isModal ? "0" : "layout.navbarDesktopHeight",
      }}
      borderBottom="1px solid"
      borderColor="border"
    >
      {/* TODO: Implement public modals */}
      <SeatingInfoModal
        isOpen={isInfoModalOpen}
        onClose={onCloseInfoModal}
        seatingObject={seatingObject}
        venueObject={venueObject}
        onHelpAndInfoOpen={onOpenHelpAndInfoModal}
        onContactDetailsOpen={onOpenContactDetailsModal}
      />

      <HelpAndInfoModalSelection
        isOpen={isHelpAndInfoModalOpen}
        onClose={onCloseHelpAndInfoModal}
        venueRefundPolicy={refundPolicyText}
      />
      <ContactDetailsModalSelection
        venueId={venueObject.id}
        isOpen={isContactDetailsModalOpen}
        onClose={onCloseContactDetailsModal}
        user={user}
        isLoggedIn={isLoggedIn || false}
      />
      <MapTimeslotModal
        isOpen={isTimeslotModalOpen}
        onClose={onCloseTimeslotModal}
        parentEventData={parentEventData}
        currentEventId={seatingObject?.id}
      />
      <SeatingMainWrapper
        useFullScreen={!refundPolicyText || hideRefundPolicy}
        padding={{ base: "10px 20px", sm: "8px 24px" }}
        borderBottom="1px"
        borderColor="gray.400"
        backgroundColor="bg"
      >
        <Flex
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          flexGrow={1}
        >
          <Flex width="100%" textAlign="left" alignItems="center">
            <Button
              data-testid="map-banner-back-to-detail-page"
              display={{
                base: isEmbedded || hideBackButton ? "none" : "flex",
                xs: "none",
              }}
              paddingInlineStart="0px !important"
              paddingInlineEnd="0px !important"
              minWidth="fit-content"
              variant="link"
              onClick={handleRedirect}
              aria-label={t("Back to event detail page")}
              fontSize="sm"
              mr="lg"
            >
              <Icon.ChevronLeft boxSize="element.xl" color="font.dark" />
            </Button>
            {/* Image Container */}
            <Flex
              display={{ base: "none", xs: hideBannerImage ? "none" : "block" }}
              mr="md"
              borderRadius="sm"
              overflow="hidden"
              width="element.9xl"
              height="fit-content"
            >
              <CardImage
                src={getImageSource()}
                alt={t("Event image", { ns: "common" })}
                height={65}
                width={130}
              />
            </Flex>
            <Flex direction="column">
              <Text
                color="font.dark"
                overflow="hidden"
                whiteSpace="normal"
                noOfLines={1}
                fontSize={{ base: "sm", md: "md" }}
                fontWeight="bold"
              >
                {seatingObject?.name}
              </Text>
              <BannerSubtitle
                seatingObject={seatingObject}
                venueObject={venueObject}
              />
              <Flex gap="xs">
                {eventStatuses.length > 0 &&
                  eventStatuses.map(
                    (status) => status.showElement && status.element
                  )}
              </Flex>
            </Flex>
          </Flex>
          {!hideInfoButton && !isEmbedded && (
            <MoreInfoButton
              seatingObject={seatingObject}
              onOpenInfoModal={onOpenInfoModal}
              onOpenTimeslotModal={onOpenTimeslotModal}
            />
          )}
        </Flex>
      </SeatingMainWrapper>
      <SeatingSideWrapper
        hidden={!refundPolicyText || hideRefundPolicy}
        bg="gray.200"
        padding={{ base: "1rem 1.25rem", sm: "8px 16px" }}
        borderLeft="1px"
        borderBottom="1px"
        borderLeftColor="gray.400"
        borderBottomColor="gray.200"
      >
        {/* Hide refund policy components if refund policy is empty */}
        <Flex alignItems="flex-start" direction="column">
          <Text fontSize="xs" fontWeight="bold" mb="2xs">
            {t("Important")}
          </Text>
          <Text
            id={refundPolicyId}
            fontSize="xs"
            overflow="hidden"
            whiteSpace="normal"
            noOfLines={2}
          >
            {refundPolicyText}
          </Text>
          <Button
            data-testid="map-banner-read-more"
            colorPalette="tertiary"
            hidden={hideReadMoreButton}
            textDecoration="underline"
            mt="2xs"
            variant="link"
            onClick={onOpenHelpAndInfoModal}
          >
            <Text fontSize="xs">{t("Read More", { ns: "common" })}</Text>
          </Button>
        </Flex>
      </SeatingSideWrapper>
    </Flex>
  );
};

export default MapBanner;
